/**
 * Facilities (review #226, Shayna 2026-10-08).
 *
 * The list: every facility — its group, its state — with her green metrics rolled up
 * over the people and months the filter bar selects, or one line per facility group.
 * Search, CSV, Enlarge. A facility or a group opens its own view (`?facility=` /
 * `?fgroup=`): the rollup, the same by department, the rollup month by month, and one
 * metric month by month by department, with Claim rows and Touch rows for it.
 *
 * Every figure is pooled from `facilityMonths` (measures/facilities.ts). A scoped
 * caller's bundle holds only their people's rows, so the page shows those people's
 * contribution to each facility, never the facility's whole book.
 */
import { isNoClaim } from '@/data/noClaim'
import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppData } from '@/data/AppData'
import { useFacilityData, FACILITY_MODELED as MODELED } from '@/data/facilityData'
import { useOpenClaimDetail } from '@/data/claimDrill'
import { useOpenTouchDetail } from '@/data/touchDrill'
import {
  bucketFacilityMonths,
  figuresOver,
  mergeBuckets,
  peopleIn,
  type FacilityBucket,
  type FacilityFigures,
  type FacilityMetricKey,
} from '@/measures/facilities'
import { COLLECTION_LABEL, collectionWindow, describeWindow } from '@/measures/collection'
import { define, type Definition } from '@/measures/definitions'
import { DataTable, type Column } from '@/components/Table'
import { ExportBar } from '@/components/ExportBar'
import { PopOut } from '@/components/PopOut'
import { Select, Stat, StatStrip } from '@/components/Bits'
import { ChartCard, LineChart, type Series } from '@/components/Charts'
import { useLoadedDataset } from '@/data/DatasetProvider'
import { FacilityLink, useOpenFacility } from '@/components/FacilityLink'
import { stateName } from '@/data/stateNames'
import { useUrlState, text, maybeText, oneOf } from '@/ui/urlState'
import type { ExportColumn } from '@/ui/export'
import { dec, int, money2, moneyK, monthSpan, monthYear, pct } from '@/format'

interface Metric {
  key: FacilityMetricKey
  label: string
  get: (f: FacilityFigures) => number | null
  fmt: (v: number | null) => string
  /** Short form for the trend's per-point labels. */
  point: (v: number) => string
  /** A count: its months add up, so a total line beside its parts only flattens them. */
  additive?: boolean
  help?: Definition
}

const pctPoint = (v: number) => `${Math.round(v * 100)}%`

/** Shayna's green metrics, in her order. */
function useMetrics(): Metric[] {
  const { label } = useAppData()
  return useMemo(
    () => [
      { key: 'costPerClaim', label: 'Cost per claim', get: (f) => f.costPerClaim, fmt: money2, point: (v) => `$${Math.round(v)}`, help: define('facilityCostPerClaim') },
      { key: 'claims', label: label('Billed claims'), get: (f) => f.claims, fmt: int, point: (v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : String(Math.round(v))), additive: true, help: define('billedClaims') },
      { key: 'collection', label: COLLECTION_LABEL, get: (f) => f.collection, fmt: (v) => pct(v), point: pctPoint, help: define('collectedPct') },
      { key: 'billedVsResolved', label: 'Claims billed vs resolved %', get: (f) => f.billedVsResolved, fmt: (v) => pct(v), point: pctPoint, help: define('facilityBilledVsResolved') },
      { key: 'zeroTouch', label: '0% touch share', get: (f) => f.zeroTouch, fmt: (v) => pct(v), point: pctPoint, help: define('zeroTouchShare') },
      { key: 'touchesPerTouched', label: 'Touches per touched claim', get: (f) => f.touchesPerTouched, fmt: (v) => dec(v), point: (v) => v.toFixed(1), help: define('touchesPerClaim') },
    ],
    [label],
  )
}

const metricColumns = <T,>(metrics: Metric[], figures: (r: T) => FacilityFigures): Column<T>[] =>
  metrics.map((m, i) => ({
    key: m.key,
    header: m.label,
    help: m.help,
    align: 'right',
    divider: i === 0,
    sort: (r) => m.get(figures(r)),
    render: (r) => <span className="mono">{m.fmt(m.get(figures(r)))}</span>,
  }))

const metricExports = <T,>(metrics: Metric[], figures: (r: T) => FacilityFigures): ExportColumn<T>[] =>
  metrics.map((m) => ({ header: m.label, value: (r) => m.get(figures(r)) }))

const METRIC_KEYS = ['costPerClaim', 'claims', 'collection', 'billedVsResolved', 'zeroTouch', 'touchesPerTouched'] as const

/** A facility or group with anything in the months: claims, labor or a collection basis. */
const active = (f: FacilityFigures) => f.claims !== 0 || f.labor !== 0 || f.charge !== 0 || f.resolved !== 0

export function Facilities() {
  const { fm } = useFacilityData()
  const [facility] = useUrlState('facility', null, maybeText)
  const [group] = useUrlState('fgroup', null, maybeText)

  if (!fm)
    return (
      <>
        <h1 className="page-title">Facilities</h1>
        <div className="notice" role="note" style={{ marginTop: 14 }}>
          <span className="eyebrow">Not in this bundle</span>
          <p className="notice-what">Facility figures arrive with the next data build.</p>
          <p className="notice-why">
            This bundle was built before the facility × month table existed. The Overview’s
            facility bars still show cost per claim by facility.
          </p>
        </div>
      </>
    )
  if (facility) return <FacilityView subject={{ facility }} />
  if (group) return <FacilityView subject={{ group }} />
  return <FacilityList />
}

/* ------------------------------------------------------------------ the list */

interface Line {
  key: string
  facility: string | null
  group: string
  state: string | null
  facilities: number
  bucket: FacilityBucket
  f: FacilityFigures
}

function FacilityList() {
  const { fm, months, weightOf, modeled, groupOf, stateOf } = useFacilityData()
  const metrics = useMetrics()
  const openFacility = useOpenFacility()
  const [show, setShow] = useUrlState('show', 'facilities', oneOf(['facilities', 'groups'] as const, 'facilities'))
  const [groupFilter, setGroupFilter] = useUrlState('group', '', text)
  const [q, setQ] = useUrlState('q', '', text)

  const byFacility = useMemo(
    () => (fm ? bucketFacilityMonths(fm, weightOf, (r) => r.facility) : new Map<string, FacilityBucket>()),
    [fm, weightOf],
  )

  const facilityLines = useMemo<Line[]>(
    () =>
      [...byFacility]
        .map(([k, b]) => ({
          key: k,
          facility: k,
          group: groupOf.get(k) ?? 'Unknown',
          state: stateOf.get(k) ?? null,
          facilities: 1,
          bucket: b,
          f: figuresOver(b, months),
        }))
        .filter((l) => active(l.f)),
    [byFacility, groupOf, stateOf, months],
  )

  const groups = useMemo(() => [...new Set(facilityLines.map((l) => l.group))].sort(), [facilityLines])

  const lines = useMemo<Line[]>(() => {
    if (show === 'facilities') return facilityLines
    const byGroup = new Map<string, Line[]>()
    for (const l of facilityLines) byGroup.set(l.group, [...(byGroup.get(l.group) ?? []), l])
    return [...byGroup].map(([g, ls]) => {
      const bucket = mergeBuckets(ls.map((l) => l.bucket))
      const states = [...new Set(ls.map((l) => l.state).filter(Boolean))] as string[]
      return {
        key: g,
        facility: null,
        group: g,
        state: states.length === 1 ? states[0]! : states.length ? `${states.length} states` : null,
        facilities: ls.filter((l) => l.facility !== 'UNKNOWN' && !isNoClaim(l.facility)).length,
        bucket,
        f: figuresOver(bucket, months),
      }
    })
  }, [show, facilityLines, months])

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase()
    return lines.filter(
      (l) =>
        (!groupFilter || l.group === groupFilter) &&
        (!needle ||
          [l.facility ?? '', l.group, l.state ?? '', l.state ? stateName(l.state) : '']
            .some((s) => s.toLowerCase().includes(needle))),
    )
  }, [lines, groupFilter, q])

  const rollup = useMemo(() => figuresOver(mergeBuckets(shown.map((l) => l.bucket)), months), [shown, months])

  const columns = useMemo<Column<Line>[]>(() => {
    const cols: Column<Line>[] = [
      {
        key: 'group',
        header: 'Facility group',
        sort: (l) => l.group,
        render: (l) => <FacilityLink group={l.group} />,
        width: show === 'facilities' ? 190 : 240,
      },
    ]
    if (show === 'facilities')
      cols.push({
        key: 'facility',
        header: 'Facility',
        sort: (l) => l.facility,
        render: (l) => <FacilityLink facility={l.facility!} />,
        width: 240,
      })
    cols.push({
      key: 'state',
      header: 'State',
      sort: (l) => l.state,
      render: (l) => (l.state ? <span title={stateName(l.state)}>{l.state}</span> : '—'),
    })
    if (show === 'groups')
      cols.push({
        key: 'facilities',
        header: 'Facilities',
        align: 'right',
        sort: (l) => l.facilities,
        render: (l) => <span className="mono">{int(l.facilities)}</span>,
        help: define('facilities'),
      })
    cols.push({
      key: 'people',
      header: 'People',
      align: 'right',
      title: 'People with claims or labor here in the months',
      sort: (l) => l.f.people,
      render: (l) => <span className="mono">{int(l.f.people)}</span>,
    })
    return [...cols, ...metricColumns<Line>(metrics, (l) => l.f)]
  }, [show, metrics])

  const exports = useMemo<ExportColumn<Line>[]>(
    () => [
      { header: 'Facility group', value: (l) => l.group },
      ...(show === 'facilities' ? [{ header: 'Facility', value: (l: Line) => l.facility }] : []),
      { header: 'State', value: (l) => l.state },
      ...(show === 'groups' ? [{ header: 'Facilities', value: (l: Line) => l.facilities }] : []),
      { header: 'People', value: (l) => l.f.people },
      { header: 'Claim labor', value: (l) => Math.round(l.f.labor * 100) / 100 },
      ...metricExports<Line>(metrics, (l) => l.f),
    ],
    [show, metrics],
  )

  return (
    <>
      <h1 className="page-title">Facilities</h1>
      <p className="body-note" style={{ marginTop: 6, fontSize: 12, color: 'var(--ink-3)' }}>
        Every facility the people in the filters billed for, {monthSpan(months)}. Open one for
        its figures by department and month by month.
        {modeled && MODELED[modeled]}
      </p>

      <section className="section">
        <RollupStrip f={rollup} metrics={metrics} months={months} />
      </section>

      <section className="section">
        <PopOut title={show === 'groups' ? 'Facility groups' : 'Facilities'}>
          {({ button, enlarged }) => (
            <>
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: '8px 12px' }}>
                {!enlarged && (
                  <h2 className="section-heading" style={{ marginRight: 'auto' }}>
                    {show === 'groups' ? 'Facility groups' : 'Facilities'}
                  </h2>
                )}
                <div style={{ minWidth: 150 }}>
                  <Select
                    label="Show"
                    value={show}
                    options={[
                      { value: 'facilities', label: 'Facilities' },
                      { value: 'groups', label: 'Facility groups' },
                    ]}
                    onChange={(v) => setShow(v as 'facilities' | 'groups')}
                  />
                </div>
                <div style={{ minWidth: 180 }}>
                  <Select
                    label="Facility group"
                    value={groupFilter}
                    options={[{ value: '', label: 'All facility groups' }, ...groups.map((g) => ({ value: g, label: g }))]}
                    onChange={setGroupFilter}
                  />
                </div>
                <label className="fac-search">
                  <span className="eyebrow">Search</span>
                  <input
                    type="search"
                    value={q}
                    placeholder="Facility, group or state"
                    onChange={(e) => setQ(e.target.value)}
                  />
                </label>
              </div>
              <ExportBar
                lead={button}
                filename={show === 'groups' ? 'facility-groups.csv' : 'facilities.csv'}
                rows={shown}
                columns={exports}
              />
              <div className="card popout-fill" style={{ padding: 0, overflow: 'hidden' }}>
                <DataTable
                  storageKey="facilities.list"
                  rows={shown}
                  columns={columns}
                  rowKey={(l) => l.key}
                  onRowClick={(l) => openFacility(l.facility ? { facility: l.facility } : { group: l.group })}
                  sortParam="sort"
                  initialSort="claims"
                  initialDir="desc"
                  maxHeight={600}
                  empty="No facility matches the filters and search."
                />
              </div>
              <p style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 9 }}>
                {int(shown.length)} {show === 'groups' ? 'facility groups' : 'facilities'} shown
                {show === 'facilities' && shown.some((l) => l.facility === 'UNKNOWN') &&
                  ' · UNKNOWN is claim labor whose facility does not resolve; it is not counted as a facility'}
                {shown.some((l) => isNoClaim(l.facility ?? l.group)) &&
                  ' · No claim is payroll that landed on no claim; it is not a facility and has no cost per claim'}
              </p>
            </>
          )}
        </PopOut>
      </section>
    </>
  )
}

/** The six figures as one strip, over whatever was pooled. */
function RollupStrip({ f, metrics, months }: { f: FacilityFigures; metrics: Metric[]; months: string[] }) {
  const win = months.length ? collectionWindow([...months].sort()[0]!, [...months].sort()[months.length - 1]!) : null
  return (
    <StatStrip cols={6}>
      {metrics.map((m, i) => (
        <Stat
          key={m.key}
          label={m.label}
          value={m.fmt(m.get(f))}
          help={m.help}
          hero={i === 0}
          note={
            m.key === 'costPerClaim'
              ? `${moneyK(f.labor)} claim labor · ${int(f.people)} people`
              : m.key === 'collection' && win
                ? describeWindow(win)
                : m.key === 'billedVsResolved'
                  ? `${int(f.resolved)} resolved`
                  : m.key === 'zeroTouch'
                    ? `of ${int(f.touchBase)} resolved`
                    : m.key === 'touchesPerTouched'
                      ? `${int(f.touches)} touches`
                      : undefined
          }
        />
      ))}
    </StatStrip>
  )
}

/* ------------------------------------------------------- one facility or group */

type Subject = { facility: string } | { group: string }

interface DeptLine {
  department: string
  bucket: FacilityBucket
  f: FacilityFigures
}

function FacilityView({ subject }: { subject: Subject }) {
  const { fm, months, weightOf, modeled, groupOf, stateOf } = useFacilityData()
  const metrics = useMetrics()
  const navigate = useNavigate()
  const openClaims = useOpenClaimDetail()
  const openTouches = useOpenTouchDetail()
  const openFacility = useOpenFacility()
  const [metricKey, setMetricKey] = useUrlState<FacilityMetricKey>(
    'metric',
    'costPerClaim',
    oneOf(METRIC_KEYS, 'costPerClaim'),
  )

  const isFacility = 'facility' in subject
  const name = isFacility ? subject.facility : subject.group
  const inSubject = useMemo(
    () =>
      isFacility
        ? (f: string) => f === name
        : (f: string) => (groupOf.get(f) ?? 'Unknown') === name,
    [isFacility, name, groupOf],
  )

  const byDept = useMemo(
    () => (fm ? bucketFacilityMonths(fm, weightOf, (r) => (inSubject(r.facility) ? r.department : null)) : new Map<string, FacilityBucket>()),
    [fm, weightOf, inSubject],
  )
  const byFacility = useMemo(
    () =>
      fm && !isFacility
        ? bucketFacilityMonths(fm, weightOf, (r) => (inSubject(r.facility) ? r.facility : null))
        : new Map<string, FacilityBucket>(),
    [fm, weightOf, inSubject, isFacility],
  )
  const all = useMemo(() => mergeBuckets(byDept.values()), [byDept])
  const rollup = useMemo(() => figuresOver(all, months), [all, months])
  const people = useMemo(() => peopleIn(all, months), [all, months])

  const deptLines = useMemo<DeptLine[]>(
    () =>
      [...byDept]
        .map(([department, bucket]) => ({ department, bucket, f: figuresOver(bucket, months) }))
        .filter((l) => active(l.f)),
    [byDept, months],
  )

  const group = isFacility ? (groupOf.get(name) ?? 'Unknown') : name
  const state = isFacility ? (stateOf.get(name) ?? null) : null
  const pick = isFacility ? { f: name } : { fg: name }

  const claimRows = (department?: string) =>
    openClaims({
      employees: department ? peopleIn(byDept.get(department), months) : people,
      label: department ? `${name} · ${department}` : name,
      pick: department ? { ...pick, wd: department } : pick,
    })

  const sortedMonths = [...months].sort()

  return (
    <>
      <div className="crumb" style={{ marginTop: 0 }}>
        <button type="button" onClick={() => navigate('/facilities')}>
          ← All facilities
        </button>
      </div>
      <h1 className="page-title">{name === 'UNKNOWN' ? 'UNKNOWN facility' : name}</h1>
      <p className="body-note" style={{ marginTop: 6, fontSize: 12, color: 'var(--ink-3)' }}>
        {isFacility ? (
          <>
            Facility group <FacilityLink group={group} />
            {state && ` · ${stateName(state)}`}
          </>
        ) : (
          <>Facility group · {int(byFacility.size)} facilities</>
        )}
        {' · '}
        {int(people.length)} people with claims or labor here · {monthSpan(months)}
        {modeled && MODELED[modeled]}
      </p>

      {!active(rollup) ? (
        <div className="empty-note">
          Nothing at {name} for the people and months in the filters.
        </div>
      ) : (
        <>
          <section className="section">
            <StatStrip cols={6}>
              {metrics.map((m, i) => (
                <Stat key={m.key} label={m.label} value={m.fmt(m.get(rollup))} help={m.help} hero={i === 0} />
              ))}
            </StatStrip>
            <div className="selbar" style={{ marginTop: 10 }}>
              <div style={{ color: 'var(--ink-3)', fontSize: 12 }}>
                {moneyK(rollup.labor)} claim labor · {int(rollup.resolved)} resolved ·{' '}
                {int(rollup.touches)} touches on {int(rollup.touched)} touched claims
              </div>
              <div className="selbar-acts">
                <button type="button" onClick={() => claimRows()}>
                  Claim rows
                </button>
                <button
                  type="button"
                  onClick={() => openTouches({ employees: people, label: name, pick })}
                >
                  Touch rows
                </button>
              </div>
            </div>
          </section>

          {!isFacility && (
            <section className="section">
              <FacilitiesInGroup byFacility={byFacility} metrics={metrics} months={months} stateOf={stateOf} onOpen={(f) => openFacility({ facility: f })} />
            </section>
          )}

          <section className="section">
            <PopOut title={`${name} by department`}>
              {({ button, enlarged }) => (
                <>
                  {!enlarged && <h2 className="section-heading">By department</h2>}
                  <ExportBar
                    lead={button}
                    filename={`${slug(name)}-by-department.csv`}
                    rows={deptLines}
                    columns={[
                      { header: 'Department', value: (l) => l.department },
                      { header: 'People', value: (l) => l.f.people },
                      { header: 'Claim labor', value: (l) => Math.round(l.f.labor * 100) / 100 },
                      ...metricExports<DeptLine>(metrics, (l) => l.f),
                    ]}
                  />
                  <div className="card popout-fill" style={{ padding: 0, overflow: 'hidden' }}>
                    <DataTable
                      storageKey="facilities.departments"
                      sortParam="dept.sort"
                      rows={deptLines}
                      columns={[
                        { key: 'department', header: 'Department', sort: (l) => l.department, render: (l) => l.department, width: 170 },
                        {
                          key: 'people',
                          header: 'People',
                          align: 'right',
                          sort: (l) => l.f.people,
                          render: (l) => <span className="mono">{int(l.f.people)}</span>,
                        },
                        ...metricColumns<DeptLine>(metrics, (l) => l.f),
                      ]}
                      rowKey={(l) => l.department}
                      onRowClick={(l) => claimRows(l.department)}
                      initialSort="claims"
                      initialDir="desc"
                      maxHeight={420}
                    />
                  </div>
                  <p style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 9 }}>
                    By the worker’s home department that month. Click a department for its claim rows.
                  </p>
                </>
              )}
            </PopOut>
          </section>

          <section className="section">
            <FacilityTrend name={name} all={all} deptLines={deptLines} metrics={metrics} months={sortedMonths} />
          </section>

          <section className="section">
            <MonthByMonth name={name} all={all} metrics={metrics} months={sortedMonths} />
          </section>

          <section className="section">
            <MonthsByDepartment
              name={name}
              deptLines={deptLines}
              all={all}
              metric={metrics.find((m) => m.key === metricKey) ?? metrics[0]!}
              metrics={metrics}
              setMetric={setMetricKey}
              months={sortedMonths}
            />
          </section>
        </>
      )}
    </>
  )
}

const slug = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'facility'

function FacilitiesInGroup({
  byFacility,
  metrics,
  months,
  stateOf,
  onOpen,
}: {
  byFacility: Map<string, FacilityBucket>
  metrics: Metric[]
  months: string[]
  stateOf: Map<string, string>
  onOpen: (facility: string) => void
}) {
  const rows = useMemo(
    () =>
      [...byFacility]
        .map(([facility, b]) => ({ facility, state: stateOf.get(facility) ?? null, f: figuresOver(b, months) }))
        .filter((r) => active(r.f)),
    [byFacility, months, stateOf],
  )
  type Row = (typeof rows)[number]
  return (
    <PopOut title="Its facilities">
      {({ button, enlarged }) => (
        <>
          {!enlarged && <h2 className="section-heading">Its facilities</h2>}
          <ExportBar
            lead={button}
            filename="group-facilities.csv"
            rows={rows}
            columns={[
              { header: 'Facility', value: (r) => r.facility },
              { header: 'State', value: (r) => r.state },
              { header: 'People', value: (r) => r.f.people },
              ...metricExports<Row>(metrics, (r) => r.f),
            ]}
          />
          <div className="card popout-fill" style={{ padding: 0, overflow: 'hidden' }}>
            <DataTable
              storageKey="facilities.facility"
              sortParam="fac.sort"
              rows={rows}
              columns={[
                { key: 'facility', header: 'Facility', sort: (r) => r.facility, render: (r) => <FacilityLink facility={r.facility} />, width: 240 },
                { key: 'state', header: 'State', sort: (r) => r.state, render: (r) => r.state ?? '—' },
                { key: 'people', header: 'People', align: 'right', sort: (r) => r.f.people, render: (r) => <span className="mono">{int(r.f.people)}</span> },
                ...metricColumns<Row>(metrics, (r) => r.f),
              ]}
              rowKey={(r) => r.facility}
              onRowClick={(r) => onOpen(r.facility)}
              initialSort="claims"
              initialDir="desc"
              maxHeight={420}
            />
          </div>
        </>
      )}
    </PopOut>
  )
}

/** Each metric down the side, each month across, and the range at the end. */
function MonthByMonth({ name, all, metrics, months }: { name: string; all: FacilityBucket; metrics: Metric[]; months: string[] }) {
  const perMonth = useMemo(() => months.map((m) => figuresOver(all, [m])), [all, months])
  const range = useMemo(() => figuresOver(all, months), [all, months])
  type Row = { metric: Metric }
  const rows: Row[] = metrics.map((metric) => ({ metric }))
  return (
    <PopOut title={`${name}, month by month`}>
      {({ button, enlarged }) => (
        <>
          {!enlarged && <h2 className="section-heading">Month by month</h2>}
          <ExportBar
            lead={button}
            filename={`${slug(name)}-by-month.csv`}
            rows={rows}
            columns={[
              { header: 'Metric', value: (r) => r.metric.label },
              ...months.map((m, i) => ({ header: m, value: (r: Row) => r.metric.get(perMonth[i]!) })),
              { header: 'Range', value: (r) => r.metric.get(range) },
            ]}
          />
          <div className="card popout-fill" style={{ padding: 0, overflow: 'hidden' }}>
            <DataTable
              storageKey="facilities.measures-by-month"
              // No sort: one row per measure, one column per month.
              rows={rows}
              columns={[
                { key: 'metric', header: 'Metric', render: (r) => r.metric.label, help: undefined, frozen: true, width: 210 },
                ...months.map<Column<Row>>((m, i) => ({
                  key: m,
                  header: monthYear(m),
                  align: 'right',
                  render: (r) => <span className="mono">{r.metric.fmt(r.metric.get(perMonth[i]!))}</span>,
                })),
                {
                  key: 'range',
                  header: 'Range',
                  align: 'right',
                  divider: true,
                  render: (r) => <b className="mono">{r.metric.fmt(r.metric.get(range))}</b>,
                },
              ]}
              rowKey={(r) => r.metric.key}
            />
          </div>
          <p style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 9 }}>
            Range is the months pooled, not their average. Each month’s collection rate is its own
            six bill months ending three before it.
          </p>
        </>
      )}
    </PopOut>
  )
}

/** One metric: each department down the side, each month across. */
function MonthsByDepartment({
  name,
  deptLines,
  all,
  metric,
  metrics,
  setMetric,
  months,
}: {
  name: string
  deptLines: DeptLine[]
  all: FacilityBucket
  metric: Metric
  metrics: Metric[]
  setMetric: (k: FacilityMetricKey) => void
  months: string[]
}) {
  type Row = { department: string; perMonth: FacilityFigures[]; range: FacilityFigures; total?: boolean }
  const rows = useMemo<Row[]>(
    () => [
      ...deptLines
        .map((l) => ({ department: l.department, perMonth: months.map((m) => figuresOver(l.bucket, [m])), range: l.f }))
        .sort((a, b) => b.range.claims - a.range.claims),
      { department: 'All departments', perMonth: months.map((m) => figuresOver(all, [m])), range: figuresOver(all, months), total: true },
    ],
    [deptLines, all, months],
  )
  return (
    <PopOut title={`${name}: ${metric.label} by department, month by month`}>
      {({ button, enlarged }) => (
        <>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: '8px 12px' }}>
            {!enlarged && (
              <h2 className="section-heading" style={{ marginRight: 'auto' }}>
                By department, month by month
              </h2>
            )}
            <div style={{ minWidth: 230 }}>
              <Select
                label="Metric"
                value={metric.key}
                options={metrics.map((m) => ({ value: m.key, label: m.label }))}
                onChange={(v) => setMetric(v as FacilityMetricKey)}
              />
            </div>
          </div>
          <ExportBar
            lead={button}
            filename={`${slug(name)}-${metric.key}-by-department-by-month.csv`}
            rows={rows}
            columns={[
              { header: 'Department', value: (r) => r.department },
              ...months.map((m, i) => ({ header: m, value: (r: Row) => metric.get(r.perMonth[i]!) })),
              { header: 'Range', value: (r) => metric.get(r.range) },
            ]}
          />
          <div className="card popout-fill" style={{ padding: 0, overflow: 'hidden' }}>
            <DataTable
              storageKey="facilities.departments-by-month"
              // No sort: one row per department, one column per month.
              rows={rows}
              columns={[
                {
                  key: 'department',
                  header: 'Department',
                  frozen: true,
                  width: 170,
                  render: (r) => (r.total ? <b>{r.department}</b> : r.department),
                },
                ...months.map<Column<Row>>((m, i) => ({
                  key: m,
                  header: monthYear(m),
                  align: 'right',
                  render: (r) => <span className="mono">{metric.fmt(metric.get(r.perMonth[i]!))}</span>,
                })),
                {
                  key: 'range',
                  header: 'Range',
                  align: 'right',
                  divider: true,
                  render: (r) => <b className="mono">{metric.fmt(metric.get(r.range))}</b>,
                },
              ]}
              rowKey={(r) => r.department}
            />
          </div>
        </>
      )}
    </PopOut>
  )
}

const MONTH_LABEL = (m: string) => new Date(`${m}-01T00:00:00`).toLocaleString('en-US', { month: 'short' })

/**
 * One metric over the months, drawn as the Overview's trend is: the facility (or group)
 * pooled, or one line per department. Each point is that month's own figure off the
 * same buckets as the tables below, so it follows the filters, the month range and the
 * Weighted switch with them.
 */
function FacilityTrend({
  name,
  all,
  deptLines,
  metrics,
  months,
}: {
  name: string
  all: FacilityBucket
  deptLines: DeptLine[]
  metrics: Metric[]
  months: string[]
}) {
  const dataset = useLoadedDataset()
  const [key, setKey] = useUrlState<FacilityMetricKey>('trend', 'costPerClaim', oneOf(METRIC_KEYS, 'costPerClaim'))
  const [lines, setLines] = useUrlState('lines', 'total', oneOf(['total', 'departments'] as const, 'total'))
  const metric = metrics.find((m) => m.key === key) ?? metrics[0]!

  const series = useMemo<Series[]>(() => {
    const line = (k: string, label: string, b: FacilityBucket): Series => {
      const per = months.map((m) => figuresOver(b, [m]))
      return { key: k, label, points: per.map((f) => metric.get(f)), counts: per.map((f) => f.claims) }
    }
    const total = line('__all__', lines === 'departments' ? 'All departments' : name, all)
    if (lines === 'total') return [total]
    const depts = [...deptLines]
      .sort((a, b) => b.f.claims - a.f.claims)
      .map((l) => line(l.department, l.department, l.bucket))
    // As on the Overview: a count's total drawn beside its parts only flattens them.
    return metric.additive || depts.length < 2 ? depts : [total, ...depts]
  }, [months, metric, lines, name, all, deptLines])

  const shadedFrom = months.findIndex((m) => dataset.openMonths.includes(m))
  const chart = (height: number) => (
    <LineChart
      series={series}
      labels={months.map(MONTH_LABEL)}
      shadedFrom={shadedFrom >= 0 ? shadedFrom : undefined}
      format={(v) => metric.fmt(v)}
      formatPoint={metric.point}
      height={height}
      labelEverySeries={series.length <= 3}
    />
  )
  return (
    <ChartCard
      title={`${name}: ${metric.label} by month`}
      note="Each month its own figure, pooled over the people in the filters. Collection is each month’s own lagged window."
      controls={
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: '8px 12px' }}>
          <h2 className="section-heading" style={{ marginRight: 'auto' }}>
            Trend
          </h2>
          <div style={{ minWidth: 230 }}>
            <Select
              label="Metric"
              value={metric.key}
              options={metrics.map((m) => ({ value: m.key, label: m.label }))}
              onChange={(v) => setKey(v as FacilityMetricKey)}
            />
          </div>
          <div style={{ minWidth: 170 }}>
            <Select
              label="Lines"
              value={lines}
              options={[
                { value: 'total', label: 'One line' },
                { value: 'departments', label: 'By department' },
              ]}
              onChange={(v) => setLines(v as 'total' | 'departments')}
            />
          </div>
        </div>
      }
      large={chart(Math.max(420, window.innerHeight - 220))}
    >
      {chart(300)}
    </ChartCard>
  )
}
